import autobind from 'autobind-decorator';
import { isEqual } from 'lodash';
import * as React from 'react';
import { connect, Dispatch } from 'react-redux';
import { AnyAction } from 'redux';
import { ModalWrapper } from '@shared/ui-components';

import { SubscriptionType } from 'common/constants/subscription';
import { State } from 'common/interfaces/state';
import { KreoDialogActions } from 'common/UIKit';
import { DialogWrapper } from 'common/UIKit/dialogs/dialog-wrapper';
import { DeferredExecutor } from 'common/utils/deferred-executer';
import { EstimateSubscriptionError } from 'unit-subscription/interfaces/estimate-subscription-error';
import { SubscriptionActions } from '../../actions/creators';
import { BillingPeriodUnit } from '../../enums/billing-period-unit';
import { EstimateSubscriptionForm } from '../../interfaces/estimate-subscription-form';
import { EstimateSubscriptionModel } from '../../interfaces/estimation-subscription-model';
import { PaymentSourcesModel } from '../../interfaces/payment-sources-model';
import { SubscriptionAddon } from '../../interfaces/subscription-addon';
import { SubscriptionDetailsModel } from '../../interfaces/subscription-details';
import { SubscriptionPlanVariantWithName } from '../../interfaces/subscription-plan-variant-with-name';
import { SubscriptionDetails } from '../subscription-details';

export const SUBSCRIBE_PAGE_APPROVE_DIALOG = 'SUBSCRIBE_PAGE_APPROVE_DIALOG';
const UPDATE_ESTIMATION_DELAY = 1000;

interface DispatchProps {
  closeDialog: () => void;
  getSubscriptionEstimation: (subscriptionType: SubscriptionType, form: EstimateSubscriptionForm) => void;
}

interface StateProps {
  isOpen: boolean;
  paymentSources: PaymentSourcesModel;
  billingEstimation: EstimateSubscriptionModel;
  billingEstimationError: EstimateSubscriptionError;
  vatNumberValidation: string;
}

interface Props extends DispatchProps, StateProps {
  onCreateSubscriptionClick: (
    quantity: number,
    paymentCardId: string,
    billingCountry: string,
    vatNumber: string,
    coupons: string[],
  ) => void;
  onSelectedAddonGroupsChanged: (addonIds: string[]) => void;
  onBillingPeriodChanged: (billingPeriod: BillingPeriodUnit) => void;
  addons: SubscriptionAddon[];
  selectedAddonIds: string[];
  billingPeriod: BillingPeriodUnit;
  planVariant: SubscriptionPlanVariantWithName;
  subscriptionType: SubscriptionType;
}

interface OwnState {
  teamSize: number;
  paymentCardId: string;
  billingCountry: string;
  vatNumber: string;
  coupons: string[];
}

class SubscribePageApproveDialogComponent extends React.Component<Props, OwnState> {
  private getEstimationDeferredExecutor: DeferredExecutor = new DeferredExecutor(UPDATE_ESTIMATION_DELAY);

  constructor(props: Props) {
    super(props);
    this.state = {
      teamSize: 2,
      paymentCardId: null,
      billingCountry: null,
      vatNumber: null,
      coupons: [],
    };
  }

  public componentDidMount(): void {
    this.getEstimationIfNeeded();
  }

  public componentDidUpdate(prevProps: Props, prevState: OwnState): void {
    this.getEstimationIfNeeded(prevState, prevProps);
    this.updateInitialStateIfAllDataIsReady(prevProps);
  }

  public render(): JSX.Element {
    if (!this.props.planVariant) {
      return null;
    }

    const paymentCardId = this.getPaymentCardId();
    const canSubscribe = paymentCardId && this.state.billingCountry;

    return (
      <DialogWrapper name={SUBSCRIBE_PAGE_APPROVE_DIALOG}>
        <ModalWrapper onExit={this.onClose}>
          <SubscriptionDetails
            estimationError={this.props.billingEstimationError}
            addons={this.props.addons}
            applyButtonCaption={this.getApplyButtonCaption()}
            currentSubscription={{
              planVariant: this.props.planVariant,
              teamSize: this.state.teamSize,
              selectedAddonIds: this.props.selectedAddonIds,
              billingPeriod: this.props.billingPeriod,
              coupons: this.state.coupons,
            }}
            header={'Review your plan and upgrade'}
            currentTeamSize={this.state.teamSize}
            minTeamSize={2}
            onApplyClick={canSubscribe ? this.onApplyClick : null}
            onSubscriptionChange={this.onSubscriptionChange}
            onChangePaymentCardId={this.onChangePaymentCardId}
            onCountryIdChanged={this.onCountryIdChanged}
            onVatNumberIdChanged={this.onVatNumberIdChanged}
            paymentCardId={paymentCardId}
            countryId={this.state.billingCountry}
            vatNumber={this.state.vatNumber}
            billingEstimation={this.props.billingEstimation}
          />
        </ModalWrapper>
      </DialogWrapper>
    );
  }

  private updateInitialStateIfAllDataIsReady(prevProps: Props): void {
    const paymentSources = this.props.paymentSources;

    if (paymentSources && !isEqual(paymentSources, prevProps.paymentSources)) {
      this.setState({
        paymentCardId: this.state.paymentCardId || paymentSources.primaryCardId,
        billingCountry: this.state.billingCountry || paymentSources.billingCountry,
        vatNumber: this.state.vatNumber || paymentSources.vatNumber,
      });
    }
  }

  private getPaymentCardId(): string {
    const paymentSources = this.props.paymentSources;
    const selected = paymentSources && paymentSources.cards.find(x => x.id === this.state.paymentCardId);
    const primaryCardId = paymentSources && paymentSources.primaryCardId;

    return selected ? selected.id : primaryCardId;
  }

  @autobind
  private onChangePaymentCardId(paymentCardId: string): void {
    this.setState({ paymentCardId });
  }

  @autobind
  private onCountryIdChanged(billingCountry: string): void {
    this.setState({ billingCountry });
  }

  @autobind
  private onVatNumberIdChanged(vatNumber: string): void {
    this.setState({ vatNumber });
  }


  @autobind
  private onSubscriptionChange(model: SubscriptionDetailsModel): void {
    if (this.props.billingPeriod !== model.billingPeriod) {
      this.props.onBillingPeriodChanged(model.billingPeriod);
    }

    if (this.props.selectedAddonIds !== model.selectedAddonIds) {
      const selectedGroupNames = this.props.addons
        .filter(x => model.selectedAddonIds.includes(x.id))
        .map(x => x.groupName);
      this.props.onSelectedAddonGroupsChanged(selectedGroupNames);
    }

    if (this.state.teamSize !== model.teamSize) {
      this.setState({ teamSize: model.teamSize });
    }

    if (this.state.coupons !== model.coupons) {
      this.setState({ coupons: model.coupons });
    }
  }

  @autobind
  private onApplyClick(): void {
    const { teamSize, billingCountry, vatNumber, coupons } = this.state;
    const paymentCardId = this.getPaymentCardId();
    this.props.onCreateSubscriptionClick(teamSize, paymentCardId, billingCountry, vatNumber, coupons);
  }

  private getApplyButtonCaption(): string {
    const variant = this.props.planVariant;
    return variant.trialPeriod
      ? `Start ${variant.trialPeriod} ${variant.trialPeriodUnit} free trial`
      : 'Start';
  }

  private getForm(
    { planVariant, selectedAddonIds }: Props,
    { teamSize, vatNumber, billingCountry, coupons }: OwnState,
  ): EstimateSubscriptionForm {
    const planId = planVariant.id;
    const quantity = planVariant.isCollaborationBlocked ? 1 : teamSize;
    const reactivate = false;

    return { addons: selectedAddonIds, quantity, planId, vatNumber, billingCountry, coupons, reactivate };
  }

  private getEstimationIfNeeded(prevState?: OwnState, prevProps?: Props): void {
    if (!this.state.billingCountry || !this.props.isOpen) {
      return;
    }

    const currentForm = this.getForm(this.props, this.state);
    const prevForm = this.getForm(prevProps, prevState);

    if (currentForm.billingCountry === prevForm.billingCountry
      && currentForm.vatNumber === prevForm.vatNumber
      && currentForm.addons === prevForm.addons
      && currentForm.planId === prevForm.planId
      && currentForm.quantity === prevForm.quantity
      && currentForm.coupons === prevForm.coupons) {
      return;
    }

    this.getEstimationDeferredExecutor.execute(() => {
      const { subscriptionType } = this.props;
      const form = this.getForm(this.props, this.state);

      this.props.getSubscriptionEstimation(subscriptionType, form);
    });
  }

  @autobind
  private onClose(): void {
    this.props.closeDialog();
  }
}


function mapStateToProps(state: State): StateProps {
  return {
    paymentSources: state.account.payment,
    billingEstimation: state.account.billingEstimation,
    isOpen: SUBSCRIBE_PAGE_APPROVE_DIALOG in state.dialog,
    vatNumberValidation: state.account.subscriptionValidation.vatNumberValidation,
    billingEstimationError: state.account.billingEstimationError,
  };
}

function mapDispatchToProps(dispatch: Dispatch<AnyAction>): DispatchProps {
  return {
    closeDialog: () => dispatch(KreoDialogActions.closeDialog(SUBSCRIBE_PAGE_APPROVE_DIALOG)),
    getSubscriptionEstimation: (subscriptionType, form) =>
      dispatch(SubscriptionActions.getCreateSubscriptionEstimation(subscriptionType, form)),
  };
}

const connector = connect(mapStateToProps, mapDispatchToProps);
export const SubscribePageApproveDialog = connector(SubscribePageApproveDialogComponent);
